import {
  IconButton as MuiIconButton,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { FormikCurrencyField, FormikTextField } from '@superdispatch/forms';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import {
  formatCarrierPaymentMethod,
  listCarrierPaymentTerms,
} from 'shared/helpers/CarrierPaymentTypes';
import { getIn, useFormikContext } from 'formik';
import { PaymentMethod, formatPaymentTerm } from '@superdispatch/sdk';
import { forwardRef, useMemo, useState } from 'react';
import { CARRIER_PAYMENT_METHODS } from 'shared/helpers/CarrierPaymentTypes';
import styled from 'styled-components';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { VehicleFormDTO } from '../data/VehiclesDTO';

const IconButton = styled(MuiIconButton)`
  padding: 0;
`;

interface VehicleSubhaulPriceProps {}

export const VehicleSubhaulPrice = forwardRef<
  HTMLInputElement,
  VehicleSubhaulPriceProps
>(function VehicleSubhaulPrice(_props, priceInputRef) {
  const { values } = useFormikContext<VehicleFormDTO>();
  const [isExpanded, setExpanded] = useState(true);
  const paymentMethod = getIn(values, 'payment_method') as PaymentMethod;

  const paymentMethods = useMemo(() => {
    // Shipper can only use superpay
    if (paymentMethod !== 'superpay') {
      return CARRIER_PAYMENT_METHODS.filter((type) => type !== 'superpay');
    }

    return CARRIER_PAYMENT_METHODS;
  }, [paymentMethod]);

  return (
    <Box paddingLeft="large" paddingRight="large">
      <Box
        padding="small"
        borderColor="Silver400"
        borderWidth="small"
        borderRadius="small"
      >
        <Stack space="small">
          <Columns>
            <Column width="content">
              <Typography variant="h5">Sub-haul Price</Typography>
            </Column>
            <Column width="fluid">
              <Inline horizontalAlign="right" space="xsmall">
                {!isExpanded && (
                  <Typography color="textSecondary">
                    {values.subhauling_price &&
                      values.subhauling_price > 0 &&
                      formatCurrency(values.subhauling_price, {
                        maximumFractionDigits: 1,
                      })}
                    {values.payment_method &&
                      ` · ${formatCarrierPaymentMethod(values.payment_method)}`}
                    {values.payment_terms &&
                      ` · ${formatPaymentTerm(values.payment_terms)}`}
                  </Typography>
                )}
                <IconButton onClick={() => setExpanded(!isExpanded)}>
                  {isExpanded ? (
                    <ExpandLess htmlColor={ColorDynamic.Dark100} />
                  ) : (
                    <ExpandMore htmlColor={ColorDynamic.Dark100} />
                  )}
                </IconButton>
              </Inline>
            </Column>
          </Columns>
          {isExpanded && (
            <Stack space="small">
              <Columns space="small">
                <Column width="1/2">
                  <FormikCurrencyField
                    fullWidth={true}
                    name="original_price"
                    label="Original Price"
                    helperText="Paid by customer"
                  />
                </Column>
                <Column width="1/2">
                  <FormikCurrencyField
                    fullWidth={true}
                    inputRef={priceInputRef}
                    name="subhauling_price"
                    label="Sub-hauling Price"
                    helperText="Paid by you to another carrier"
                  />
                </Column>
              </Columns>
              <Columns space="small">
                <Column width="1/2">
                  <FormikTextField
                    select={true}
                    fullWidth={true}
                    name="payment_method"
                    label="Payment Method"
                  >
                    {paymentMethods.map((key) => (
                      <MenuItem key={key} value={key}>
                        {formatCarrierPaymentMethod(key)}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Column>
                <Column width="1/2">
                  <FormikTextField
                    select={true}
                    fullWidth={true}
                    name="payment_terms"
                    label="Payment Terms"
                  >
                    {listCarrierPaymentTerms(paymentMethod).map((key) => {
                      return (
                        <MenuItem key={key} value={key}>
                          {formatPaymentTerm(key)}
                        </MenuItem>
                      );
                    })}
                  </FormikTextField>
                </Column>
              </Columns>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
});
